import React from "react";
import { Box } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import ApiService from "../services/api";
import { cookieHelpers } from "../utils/cookieUtils";
import { isTokenExpired } from "../utils/tokenUtils";
import { VIEWS } from "../constants/views";
import { useSessionTracking } from "./hooks/useSessionTracking";

// Import pages
import DistributorLoginPage from "../pages/distributor/DistributorLoginPage";
import DistributorWelcomePage from "../pages/distributor/DistributorWelcomePage";
import DistributorSuccessful from "../pages/distributor/DistributorSussesful";
import UserInfoPage from "../pages/user/UserInfoPage";
import EditProfilePage from "../pages/user/EditProfilePage";
import SelfieCapturePage from "../pages/analysis/SelfieCapturePage";
import ScoresPage from "../pages/analysis/ScoresPage";
import FeedbackPage from "../pages/analysis/FeedbackPage";
import ErrorPage from "../pages/ErrorPage";
import Welcome from "../pages/customer/WelcomePage";
import MoreUserDetails from "../pages/more-user-details";
import DistributorUserDetails from "../pages/distributor/DistributorUserDetails";
import DistributorDeactivatedUser from "../components/DistributorDeactivatedUser";
import PrivacyPoliciesPage from "../pages/PrivacyPoliciesPage";

const DistributorFlow: React.FC = () => {
  const {
    currentView,
    sessionId,
    setCurrentView,
    setToken,
    setRole,
    fetchUserProfiles,
    setDistributorId,
    setEnableSessionToCreateNew,
  } = useAppContext();
  const location = useLocation();

  // Track session view changes
  useSessionTracking(currentView, sessionId);

  // Check if accessing /distributor/successful
  React.useEffect(() => {
    const pathname = location.pathname;
    
    if (pathname === '/distributor/successful') {
      setCurrentView(VIEWS.DISTRIBUTOR_SUCCESSFUL);
      return;
    }
  }, [location.pathname, setCurrentView]);

  React.useEffect(() => {
    const checkTokenAndRedirect = async () => {
      const storedToken = cookieHelpers.getToken('distributor');
      if (storedToken) {
        try {
          const api = new ApiService(null, storedToken);

          const userProfiles = (await fetchUserProfiles(api, 1)) as any;
          if (userProfiles && userProfiles.user) {
            setDistributorId(userProfiles.user._id);
            setEnableSessionToCreateNew(true);
          }

          setToken(storedToken);
          setCurrentView(VIEWS.DISTRIBUTOR_WELCOME);
        } catch (error) {
          console.error("Token validation failed:", error);
          // Token is invalid, remove it
          cookieHelpers.removeToken();
          setToken(null);
          setEnableSessionToCreateNew(false);
          // Only redirect to login if token validation actually fails
          setCurrentView(VIEWS.DISTRIBUTOR_LOGIN);
        }
      }
    };

    checkTokenAndRedirect();
  }, [sessionId, setToken, setRole, setCurrentView, setDistributorId]);

  // Initial token check for distributor flow
  React.useEffect(() => {
    const isDistributorFlow =
      location.pathname.startsWith("/distributor") ||
      location.pathname === "" ||
      location.pathname === "/";
    const storedToken = cookieHelpers.getToken('distributor');
    if (storedToken && isDistributorFlow && !isTokenExpired(storedToken)) {
      setCurrentView(VIEWS.DISTRIBUTOR_WELCOME);
    }
  }, [location.pathname]);

  // Simple back button prevention
  React.useEffect(() => {
    const ssoLoginSuccess = sessionStorage.getItem('ssoLoginSuccess');
    
    // Clear SSO login flag after first load
    if (ssoLoginSuccess === 'true') {
      sessionStorage.removeItem('ssoLoginSuccess');
    }

    // Handle back button on welcome page
    const handlePopState = () => {
      const storedToken = cookieHelpers.getToken('distributor');
      const isDistributorWelcome = currentView === VIEWS.DISTRIBUTOR_WELCOME;
      
      if (storedToken && !isTokenExpired(storedToken) && isDistributorWelcome) {
        // Keep user on welcome page
        window.history.pushState(null, '', '/distributor');
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentView]);

  const renderView = () => {
    switch (currentView) {
      case VIEWS.DISTRIBUTOR_LOGIN:
        return <DistributorLoginPage />;
      case VIEWS.DISTRIBUTOR_WELCOME:
        return <DistributorWelcomePage />;
      case VIEWS.DISTRIBUTOR_SUCCESSFUL:
        return <DistributorSuccessful />;
      case VIEWS.USER_INFO:
        return <UserInfoPage />;
      case VIEWS.EDIT_PROFILE:
        return <EditProfilePage />;
      case VIEWS.SELFIE:
        return <SelfieCapturePage />;
      case VIEWS.SCORES:
        return <ScoresPage />;
      case VIEWS.FEEDBACK:
        return <FeedbackPage />;
      case VIEWS.ERROR:
        return <ErrorPage />;
      case VIEWS.WELCOME:
        return <Welcome />;
      case VIEWS.MORE_USER_DETAILS:
        return <MoreUserDetails />;
      case VIEWS.USER_DETAILS:
        return <DistributorUserDetails />;
      case VIEWS.DISTRIBUTOR_USER_DETAILS:
        return <DistributorUserDetails />;
      case VIEWS.DISTRIBUTOR_DEACTIVATED_USER:
        return <DistributorDeactivatedUser />;
      case VIEWS.PRIVACY_POLICY:
        return <PrivacyPoliciesPage />;
      default:
        return <DistributorLoginPage />;
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {renderView()}
    </Box>
  );
};

export default DistributorFlow;
